import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Timesheet } from '../../../models/timesheet';
import { NGXLogger } from 'ngx-logger';
import moment from 'moment';
import _ from 'lodash';
import { TimesheetRecord } from '../../../models/timesheet-record';
import { TableColumn } from '../../../models/table-column';
import { TimesheetService } from '../../../services/timesheet.service';
import { AuxiliarService } from '../../../services/auxiliar.service';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { Employee } from '../../../models/employee';
import { Absence } from '../../../models/absence';
import { roles } from '../../../utils/enums/roles';
import { AbsenceService } from '../../../services/absence.service';

@Component({
  selector: 'app-timesheet-drawer',
  standalone: false,
  templateUrl: './timesheet-drawer.component.html',
  styleUrl: './timesheet-drawer.component.scss'
})
export class TimesheetDrawerComponent implements OnInit, OnDestroy {
  @Input() absences: Absence[] = [];
  @Input() employee: Employee | undefined;
  @Input() timesheet: Timesheet;
  @Input() timesheetsMonthHours: number = 0;

  public _ = _;
  public absenceTypes: {name: string, value: string}[] = [];
  public allowEdit: boolean;
  public columns: TableColumn[] = [];
  private destroy$: Subject<boolean> = new Subject<boolean>();
  public hoursDay: string = '00:00';
  public hoursMonth: string = '00:00';
  public loadingAbsences: boolean = true;
  public loadingRecords: boolean = true;
  public records: TimesheetRecord[] = [];
  public showAbsenceComments: boolean;
  public statusOptions: {name: string, value: string}[] = [];

  constructor(
    private logger: NGXLogger,
    private auxiliarService: AuxiliarService,
    private timesheetService: TimesheetService,
    private translate: TranslateService,
    private absenceService: AbsenceService,
  ){}

  ngOnInit(): void {
    this.logger.debug('selected timesheet: ', this.timesheet);
    const timesheet = _.cloneDeep(this.timesheet);
    // calc day total hours
    const timeworkedDay = moment.duration(_.get(timesheet, 'timeworked', 0), 'minutes');
    this.hoursDay = `${Math.floor(timeworkedDay.asHours()).toString().padStart(2,'0')}:${timeworkedDay.minutes().toString().padStart(2,'0')}`;
    // calc month total hours
    const timeworkedMonth = moment.duration(this.timesheetsMonthHours, 'minutes');
    this.hoursMonth = `${Math.floor(timeworkedMonth.asHours()).toString().padStart(2,'0')}:${timeworkedMonth.minutes().toString().padStart(2,'0')}`;
    // status options
    this.statusOptions = this.auxiliarService.getTimesheetRecordStatusOptions();
    // format timesheet records
    this.records = _.orderBy(_.map(timesheet.records, (record: TimesheetRecord): TimesheetRecord => {
      return {
        ...record,
        begin: {
          ...record.begin,
          recordRaw: _.get(record, 'begin.record'),
          record: _.get(record, 'begin.record') ? moment(_.get(record, 'begin.record')).format('HH:mm') : '',
          status: _.find(this.statusOptions, {value: _.get(record, 'begin.status')}),
        },
        end: {
          ...record.end,
          record: _.get(record, 'end.record') ? moment(_.get(record, 'end.record')).format('HH:mm') : '',
          status: _.find(this.statusOptions, {value: _.get(record, 'end.status')}),
        },
        icon: this.timesheetService.getTimesheetRecordIcon(record),
      };
    }), 'begin.recordRaw');
    this.loadingRecords = false;
    // absences
    this.showAbsenceComments = _.includes(roles, 'manager');
    this.allowEdit = !!(_.includes(roles, 'manager') && this.employee);
    this.absenceTypes = this.auxiliarService.getAbsenceTypes();
    this.absences = _.orderBy(_.map(this.absences, (absence: Absence) => {
      const type = _.find(this.absenceTypes, {value: absence.type});
      return {
        ...absence,
        hourBegin: moment(absence.begin).format('HH:mm'),
        hourEnd: moment(absence.begin).add(absence.duration, 'minute').format('HH:mm'),
        typeName: type?.name,
      };
    }), 'begin');
    this.loadingAbsences = false;
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  public async confirmRecords(record: TimesheetRecord) {
    let confirmBegin: any = undefined;
    let confirmEnd: any = undefined;
    if(_.get(record, 'begin.id') && _.get(record, 'begin.status.value') === 'manual') {
      try {
        confirmBegin = await firstValueFrom(this.timesheetService.confirmTimesheetRecord(record.begin.id, this.employee));
      } catch (err) {
        confirmBegin = undefined;
        this.auxiliarService.toast(this.translate.instant('error.timesheet-record-begin-confirm'), 'error');
      }
    }
    if(_.get(record, 'end.id') && _.get(record, 'end.status.value') === 'manual') {
      try {
        confirmEnd = await firstValueFrom(this.timesheetService.confirmTimesheetRecord(record.end.id, this.employee));
      } catch (err) {
        confirmEnd = undefined;
        this.auxiliarService.toast(this.translate.instant('error.timesheet-record-end-confirm'), 'error');
      }
    }
    if(!confirmBegin && !confirmEnd) return;
    if(confirmBegin) {
      _.set(record, 'begin.status', _.find(this.statusOptions, {value: 'confirmed'}));
    }
    if(confirmEnd) {
      _.set(record, 'end.status', _.find(this.statusOptions, {value: 'confirmed'}));
    }
    // update calendar
    this.timesheetService.updateUserTimesheets(this.timesheet);
  }

  public async rejectRecords(record: TimesheetRecord) {
    let rejectBegin: any = undefined;
    let rejectEnd: any = undefined;
    if(_.get(record, 'begin.id') && _.get(record, 'begin.status.value') === 'manual') {
      try {
        rejectBegin = await firstValueFrom(this.timesheetService.rejectTimesheetRecord(record.begin.id, this.employee));
      } catch (err) {
        rejectBegin = undefined;
        this.auxiliarService.toast(this.translate.instant('error.timesheet-record-begin-reject'), 'error');
      }
    }
    if(_.get(record, 'end.id') && _.get(record, 'end.status.value') === 'manual') {
      try {
        rejectEnd = await firstValueFrom(this.timesheetService.rejectTimesheetRecord(record.end.id, this.employee));
      } catch (err) {
        rejectEnd = undefined;
        this.auxiliarService.toast(this.translate.instant('error.timesheet-record-end-reject'), 'error');
      }
    }
    if(!rejectBegin && !rejectEnd) return;
    if((rejectBegin && rejectEnd) || (rejectBegin && !record.end?.id) || (rejectEnd && !record.begin?.id)) {
      _.remove(this.records, r => r === record);
    } else if(rejectBegin) {
      delete record.begin;
    } else if(rejectEnd) {
      delete record.end;
    }
    // recalc hoursDay && hoursMonth
    this.recalcTotals(record);
    // update calendar
    this.timesheetService.updateUserTimesheets(this.timesheet);
  }

  public async deleteRecord(record: TimesheetRecord) {
    let removeBegin: any = undefined;
    let removeEnd: any = undefined;
    if(_.get(record, 'begin.id')) {
      try {
        removeBegin = await firstValueFrom(this.timesheetService.deleteTimesheetRecord(record.begin.id, this.employee));
      } catch (err) {
        removeBegin = undefined;
        this.auxiliarService.toast(this.translate.instant('error.timesheet-record-begin-delete'), 'error');
      }
    }
    if(_.get(record, 'end.id')) {
      try {
        removeEnd = await firstValueFrom(this.timesheetService.deleteTimesheetRecord(record.end.id, this.employee));
      } catch (err) {
        removeEnd = undefined;
        this.auxiliarService.toast(this.translate.instant('error.timesheet-record-end-delete'), 'error');
      }
    }
    if(!removeBegin && !removeEnd) return;
    if((removeBegin && removeEnd) || (removeBegin && !record.end?.id) || (removeEnd && !record.begin?.id)) {
      _.remove(this.records, r => r === record);
    } else if(removeBegin) {
      delete record.begin;
    } else if(removeEnd) {
      delete record.end;
    }
    // recalc hoursDay && hoursMonth
    this.recalcTotals(record);
    // update calendar
    this.timesheetService.updateUserTimesheets(this.timesheet);
    // check if it is necessary to update user last timesheet in header
    if(moment().isSame(moment(this.timesheet.date), 'date')) {
      this.timesheetService.updateUserLastTimesheet();
    }
  }

  private recalcTotals(record: TimesheetRecord): void {
    if(record.type === 'work') {
      const timesheet: Timesheet = _.cloneDeep(this.timesheet);
      // day total hours
      const timeworkedDay = moment.duration(_.get(timesheet, 'timeworked', 0) - _.get(record, 'duration', 0), 'minutes');
      this.hoursDay = `${Math.floor(timeworkedDay.asHours()).toString().padStart(2,'0')}:${timeworkedDay.minutes().toString().padStart(2,'0')}`;
      // month total hours
      const timeworkedMonth = moment.duration(this.timesheetsMonthHours - _.get(record, 'duration', 0), 'minutes');
      this.hoursMonth = `${Math.floor(timeworkedMonth.asHours()).toString().padStart(2,'0')}:${timeworkedMonth.minutes().toString().padStart(2,'0')}`;
    }
  }

  public editAbsence(absence: Absence): void {
    if(!this.employee) return;
    this.absenceService.openAbsenceDetail(this.employee, undefined, 'left', absence)
      .then((res: any) => {
        if(res) {
          const updatedAbsence = _.get(res, 'data');
          // update absences table
          const type = _.find(this.absenceTypes, {value: absence.type});
          this.absences = _.map(this.absences, (a) => {
            return a.id === updatedAbsence.id ? {
              ...updatedAbsence,
              hourBegin: moment(absence.begin).format('HH:mm'),
              hourEnd: moment(absence.begin).add(absence.duration, 'minute').format('HH:mm'),
              typeName: type?.name,
            } : a;
          });
          // update calendar
          this.absenceService.updateEmployeeAbsences(absence);
        };
      });
  }

  public deleteAbsence(absence: Absence): void {
    this.absenceService.deleteAbsence(absence, this.employee).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (res: any) => {
        this.logger.debug('[absenceService] deleteAbsence res: ', res);
        // update absences table
        _.remove(this.absences, a => a === absence);
        // update calendar
        this.absenceService.updateEmployeeAbsences(absence);
      },
      error: (err: any) => {
        this.logger.error('[absenceService] deleteAbsence err:', err);
        this.auxiliarService.toast(this.translate.instant('error.absence-delete'), 'error');
      }
    });
  }
}
